import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Interviews() {
  const [interviews, setInterviews] = useState([]);
  const [view, setView] = useState('calendar');
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const load = () => {
    const from = new Date(month.getFullYear(), month.getMonth(), 1).toISOString().slice(0, 10);
    const to = new Date(month.getFullYear(), month.getMonth() + 1, 0).toISOString().slice(0, 10);
    api(`/ats/interviews?from=${from}&to=${to}`).then(r => r.ok ? r.json() : []).then(setInterviews).catch(() => {});
  };
  useEffect(() => { load(); }, [month]);

  const prevMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const nextMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const today = new Date();

  // Build calendar grid
  const firstDay = month.getDay();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const interviewsByDay = {};
  interviews.forEach(iv => {
    if (!iv.scheduledAt) return;
    const day = new Date(iv.scheduledAt).getDate();
    if (!interviewsByDay[day]) interviewsByDay[day] = [];
    interviewsByDay[day].push(iv);
  });

  const statusColor = (s) => s === 'completed' ? 'bg-green-500' : s === 'cancelled' ? 'bg-red-400' : 'bg-blue-500';
  const monthLabel = month.toLocaleDateString(undefined, {month: 'long', year: 'numeric'});

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Interviews</h2>
        <div className="flex gap-2 items-center">
          <button onClick={() => setView('calendar')} className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'calendar' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}>Calendar</button>
          <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'list' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}>List</button>
        </div>
      </div>

      {view === 'calendar' ? (
        <div className="bg-white rounded-lg shadow">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <button onClick={prevMonth} className="text-gray-500 hover:text-gray-700 text-lg font-medium">←</button>
            <h3 className="text-lg font-semibold text-gray-900">{monthLabel}</h3>
            <button onClick={nextMonth} className="text-gray-500 hover:text-gray-700 text-lg font-medium">→</button>
          </div>
          <div className="grid grid-cols-7">
            {DAYS.map(d => <div key={d} className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b">{d}</div>)}
            {cells.map((day, i) => {
              const isToday = day && month.getMonth() === today.getMonth() && month.getFullYear() === today.getFullYear() && day === today.getDate();
              const dayInterviews = day ? (interviewsByDay[day] || []) : [];
              return (
                <div key={i} className={`min-h-[100px] border-b border-r p-1 ${day ? '' : 'bg-gray-50'}`}>
                  {day && (
                    <>
                      <div className={`text-xs font-medium mb-1 ${isToday ? 'bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-500 px-1'}`}>{day}</div>
                      <div className="space-y-0.5">
                        {dayInterviews.map(iv => (
                          <Link key={iv.id} to={`/ats/applicants/${iv.applicantId}`}
                            className={`block px-1.5 py-0.5 rounded text-xs text-white truncate ${statusColor(iv.status)}`}
                            title={`${iv.applicantName} - ${iv.interviewer || ''}`}>
                            {new Date(iv.scheduledAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} {iv.applicantName}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interviewer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {interviews.map(iv => (
                <tr key={iv.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4"><Link to={`/ats/applicants/${iv.applicantId}`} className="text-teal-600 hover:text-teal-800 font-medium">{iv.applicantName}</Link></td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{iv.jobTitle || '—'}</td>
                  <td className="px-6 py-4 text-gray-600">{iv.interviewer || '—'}</td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{iv.scheduledAt ? new Date(iv.scheduledAt).toLocaleString() : '—'}</td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{iv.type || '—'}</td>
                  <td className="px-6 py-4"><span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${iv.status === 'completed' ? 'bg-green-50 text-green-700' : iv.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>{iv.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {interviews.length === 0 && <div className="text-center py-12 text-gray-500">No interviews this month.</div>}
        </div>
      )}
    </Layout>
  );
}
